import axios from 'axios';

// Auth token + user info live in sessionStorage (cleared on tab close) instead
// of localStorage to reduce XSS persistence surface. The proper hardening is
// httpOnly cookies (tracked as P3 backlog item) which would require backend
// Set-Cookie + CORS withCredentials + frontend rewrite.
const tokenStore = {
  get: () => {
    try { return sessionStorage.getItem('canales_token'); }
    catch { return null; }
  },
  set: (v) => { try { sessionStorage.setItem('canales_token', v); } catch { /* ignore */ } },
  clear: () => {
    try {
      sessionStorage.removeItem('canales_token');
      sessionStorage.removeItem('canales_user');
    } catch { /* ignore */ }
  },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios paramsSerializer: by default arrays are serialized as `?key[]=v1&key[]=v2`,
// but FastAPI's `List[str] = Query(None)` expects the *repeat* form (`?key=v1&key=v2`).
// `{ indexes: null }` produces the latter. Required for the global FiltersBar
// (brand[], category[], region[]) to actually reach the backend.
export const api = axios.create({
  baseURL: API,
  paramsSerializer: { indexes: null },
});

api.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Attach the current workspace to every report-aware request so the
  // backend can filter the data sandbox. The frontend writes this key from
  // WorkspaceContext when the user switches.
  try {
    const ws = sessionStorage.getItem('canales_current_workspace');
    if (ws) {
      config.params = { ...(config.params || {}) };
      if (config.params.workspace === undefined) {
        config.params.workspace = ws;
      }
    }
  } catch { /* ignore */ }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response && err.response.status === 401) {
      // expired/invalid token: clear and force re-login — but only on
      // authenticated routes. Public routes like /d/:token must not redirect
      // nor clear the admin's session (they might be checking the link
      // from the same browser where they are logged in).
      const path = window.location.pathname || '';
      const isPublicRoute = path === '/' || path.startsWith('/d/') || path.startsWith('/p/') || path === '/wall';
      if (!isPublicRoute) {
        tokenStore.clear();
        window.location.assign('/');
      }
    }
    return Promise.reject(err);
  }
);

export { tokenStore };
export default api;

// Domain helpers
export const authApi = {
  login: (email, password, legal_version) =>
    api.post('/auth/login', { email, password, ...(legal_version ? { legal_version } : {}) }).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
};

export const storesApi = {
  list: () => api.get('/stores').then((r) => r.data),
  detail: (id) => api.get(`/stores/${id}`).then((r) => r.data),
};

export const sellersApi = {
  list: (store, extra = {}) => {
    const params = { ...extra };
    if (store && store !== 'todas') params.store = store;
    return api.get('/sellers', { params }).then((r) => r.data);
  },
  create: (data) => api.post('/sellers', data).then((r) => r.data),
  update: (id, data) => api.patch(`/sellers/${id}`, data).then((r) => r.data),
};

export const ticketsApi = {
  list: (params = {}) => api.get('/tickets', { params }).then((r) => r.data),
  create: (data) => api.post('/tickets', data).then((r) => r.data),
};

// Build axios params: merge per-call params with store + global filters
function _params(store, extra = {}) {
  const out = { ...extra };
  if (store && store !== 'todas') out.store = store;
  return out;
}

export const reportsApi = {
  kpis: (store, extra = {}) => api.get('/reports/kpis', { params: _params(store, extra) }).then((r) => r.data),
  daily: (store, extra = {}) => api.get('/reports/daily', { params: _params(store, extra) }).then((r) => r.data),
  monthly: (store, extra = {}) => api.get('/reports/monthly', { params: _params(store, extra) }).then((r) => r.data),
  categories: (store, extra = {}) => api.get('/reports/categories', { params: _params(store, extra) }).then((r) => r.data),
  activity: (store, extra = {}) => api.get('/reports/activity', { params: _params(store, extra) }).then((r) => r.data),
  merchandise: (store, extra = {}) => api.get('/reports/merchandise', { params: _params(store, extra) }).then((r) => r.data),
  filterOptions: () => api.get('/reports/filter-options').then((r) => r.data),
  // Iter 96.8 — Meta mes vs Vendido por tienda (vista global de cumplimiento)
  goalsVsActual: (params = {}) => api.get('/reports/goals-vs-actual', { params }).then((r) => r.data),
  // Iter 96.8d — Histórico anual: 12 meses de cumplimiento
  goalsYearly: (params = {}) => api.get('/reports/goals-vs-actual/yearly', { params }).then((r) => r.data),
  // Top 10 / Bottom 10 rankings
  top10Sellers: (store, extra = {}) => api.get('/reports/top-10/sellers', { params: _params(store, extra) }).then((r) => r.data),
  top10Products: (store, extra = {}) => api.get('/reports/top-10/products', { params: _params(store, extra) }).then((r) => r.data),
  top10Categories: (store, extra = {}) => api.get('/reports/top-10/categories', { params: _params(store, extra) }).then((r) => r.data),
  top10Companies: (store, extra = {}) => api.get('/reports/top-10/companies', { params: _params(store, extra) }).then((r) => r.data),
  top10Stores: (store, extra = {}) => api.get('/reports/top-10/stores', { params: _params(store, extra) }).then((r) => r.data),
  top10Brands: (store, extra = {}) => api.get('/reports/top-10/brands', { params: _params(store, extra) }).then((r) => r.data),
  bottom10Sellers: (store, extra = {}) => api.get('/reports/bottom-10/sellers', { params: _params(store, extra) }).then((r) => r.data),
  writtenVsCollected: (store) => api.get('/reports/written-vs-collected', { params: _params(store) }).then((r) => r.data),
};

// Podium (separate platform: reviews + messaging + payments)
export const podiumApi = {
  overview: () => api.get('/podium/overview').then((r) => r.data),
  locations: () => api.get('/podium/locations').then((r) => r.data),
  reviews: (limit = 10) => api.get('/podium/reviews', { params: { limit } }).then((r) => r.data),
  messaging: () => api.get('/podium/messaging').then((r) => r.data),
  payments: () => api.get('/podium/payments').then((r) => r.data),
  employees: () => api.get('/podium/employees').then((r) => r.data),
  mappingList: () => api.get('/podium/mapping').then((r) => r.data),
  mappingSet: (podium_location, storis_store_code) => api.post('/podium/mapping', { podium_location, storis_store_code }).then((r) => r.data),
  sellerBadges: () => api.get('/podium/seller-badges').then((r) => r.data),
};

export const uploadsApi = {
  // --- Background-upload persistence ---------------------------------------
  // Iter 97.2 — Persist the in-flight job so a completed import is never lost
  // when the browser disconnects / the tab is closed / the user navigates away
  // during a long import. We store the jobId in localStorage when an upload
  // starts and clear it only on a terminal status (completed/failed). On
  // returning to /cargas the page calls checkPending() to surface the result.
  _PENDING_KEY: 'canales_pending_upload',
  _setPending(info) {
    try { localStorage.setItem(uploadsApi._PENDING_KEY, JSON.stringify(info)); } catch (e) { /* no-op */ }
  },
  _clearPending() {
    try { localStorage.removeItem(uploadsApi._PENDING_KEY); } catch (e) { /* no-op */ }
  },
  _getPending() {
    try {
      const v = localStorage.getItem(uploadsApi._PENDING_KEY);
      return v ? JSON.parse(v) : null;
    } catch (e) { return null; }
  },
  clearPending() { uploadsApi._clearPending(); },
  /**
   * Check whether a background upload finished while the user was away.
   * Returns null if there is no pending job, or { jobId, filename, status, ... }.
   * Stale records (> 30 min) are dropped. Server-unreachable keeps the record.
   */
  async checkPending() {
    const p = uploadsApi._getPending();
    if (!p?.jobId) return null;
    if (p.startedAt && Date.now() - p.startedAt > 30 * 60 * 1000) {
      uploadsApi._clearPending();
      return null;
    }
    try {
      const status = await api.get(`/uploads/import-status/${p.jobId}`).then((r) => r.data);
      if (status.status === 'completed' || status.status === 'failed') uploadsApi._clearPending();
      return { ...p, ...status };
    } catch (e) {
      // Job gone (404) → drop it; otherwise keep for next visit.
      if (e?.response?.status === 404) { uploadsApi._clearPending(); return null; }
      return { ...p, status: 'unknown' };
    }
  },
  /**
   * Upload a Storis report (XLSX/CSV).
   *
   * Iter 81 — Background processing.
   * The backend returns a `job_id` immediately and parses the file
   * asynchronously. This helper hides that change from callers: it polls
   * `import-status` every 2 s until the job completes and then resolves
   * with the final upload doc (same shape as the legacy synchronous
   * response, so the existing UI code keeps working unchanged).
   *
   * Iter 97 — Chunked uploads for files > 5 MB.
   * Production proxies (canalesanalisis.com) reject multipart bodies
   * above ~10 MB / 30 s with a generic "Error al cargar archivo". When
   * the file exceeds the inline threshold we transparently switch to
   * the /api/uploads/chunked/* flow (2 MB per request). Callers don't
   * need to do anything different.
   *
   * For duplicate files the backend still responds 409 SYNCHRONOUSLY
   * (before queuing), so the caller's catch block continues to work.
   *
   * @param {File} file
   * @param {string} [store]
   * @param {boolean} [replace] confirm a duplicate replacement
   * @param {(message: string, rows?: number) => void} [onProgress]
   * @param {string} [mode]  'upsert_period' (surgical, default) | 'replace_all'
   */
  upload: async (file, store, replace = false, onProgress, mode = 'upsert_period') => {
    const INLINE_LIMIT = 5 * 1024 * 1024; // 5 MB inline → above that, use chunks
    const CHUNK_BYTES = 2 * 1024 * 1024;  // 2 MB per chunk

    let enqueueData;

    if (file.size <= INLINE_LIMIT) {
      // ----- Small file: single multipart upload (legacy path) -----
      const fd = new FormData();
      fd.append('file', file);
      const params = {
        ...(store ? { store } : {}),
        ...(replace ? { replace: true } : {}),
        mode,
      };
      const resp = await api.post('/uploads/report', fd, {
        params,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Legacy synchronous response (no background job): return directly.
      if (!resp.data?.job_id) return resp.data;
      enqueueData = resp.data;
    } else {
      // ----- Big file: chunked upload (production-safe path) -----
      if (onProgress) onProgress('Iniciando carga por chunks...', 0);

      const initParams = {
        filename: file.name,
        total_size: file.size,
        ...(store ? { store } : {}),
        ...(replace ? { replace: true } : {}),
        mode,
      };
      const initResp = await api.post('/uploads/chunked/init', null, { params: initParams });
      const sessionId = initResp.data.session_id;
      const totalChunks = Math.ceil(file.size / CHUNK_BYTES);

      for (let i = 0; i < totalChunks; i += 1) {
        const start = i * CHUNK_BYTES;
        const end = Math.min(start + CHUNK_BYTES, file.size);
        const blob = file.slice(start, end);
        const fd = new FormData();
        // Backend reads bytes from this multipart "file" part.
        fd.append('file', blob, `chunk-${i}`);
        // Each chunk is small (≤ 2 MB) → safe under any proxy limit.
        await api.post(`/uploads/chunked/${sessionId}/chunk`, fd, {
          params: { index: i },
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (onProgress) {
          const pct = Math.round(((i + 1) / totalChunks) * 100);
          onProgress(`Subiendo... ${pct}%  (${i + 1}/${totalChunks})`, 0);
        }
      }

      if (onProgress) onProgress('Ensamblando archivo en el servidor...', 0);
      const finalizeResp = await api.post(`/uploads/chunked/${sessionId}/finalize`);
      enqueueData = finalizeResp.data;
    }

    if (!enqueueData?.job_id) return enqueueData;
    const jobId = enqueueData.job_id;
    // Persist the job so a completed import survives a disconnect / tab close.
    uploadsApi._setPending({ jobId, filename: file?.name || enqueueData.filename || 'archivo', startedAt: Date.now() });
    if (onProgress) onProgress(enqueueData.message || 'Importación iniciada...', 0);

    // Poll every 2 s. No hard timeout — slow imports can take 2–5 min.
    // Iter 97.1 — Resilient polling: the import runs as a background task on the
    // server, so a transient network blip / gateway 5xx / backend reload during a
    // multi-minute import must NOT abort the whole upload. We tolerate up to
    // MAX_POLL_FAILURES *consecutive* transient errors (reconnecting silently) and
    // only surface the error if the server stays unreachable that long. The
    // counter resets on every successful poll. (Root cause of the reported
    // "Error de conexión al verificar el estado: Network Error": a single failed
    // poll aborted the UX even though the import had actually completed.)
    const MAX_POLL_FAILURES = 15; // ≈30-45 s of grace
    let pollFailures = 0;
    while (true) {
      await new Promise((r) => setTimeout(r, 2000));
      let status;
      try {
        status = await api.get(`/uploads/import-status/${jobId}`).then((r) => r.data);
        pollFailures = 0;
      } catch (pollErr) {
        const httpStatus = pollErr?.response?.status;
        // No HTTP response (axios "Network Error") or a gateway/server 5xx is
        // transient — the import keeps running server-side, so keep polling.
        const transient = !httpStatus || httpStatus >= 500;
        if (transient) {
          pollFailures += 1;
          if (pollFailures < MAX_POLL_FAILURES) {
            if (onProgress) {
              onProgress(
                `Reconectando con el servidor… (${pollFailures}/${MAX_POLL_FAILURES}) · la importación sigue en segundo plano`,
                0,
              );
            }
            continue;
          }
        }
        // Iter 96.4 — Surface network/server errors during polling so the
        // user sees something better than the generic "Error al cargar archivo"
        // when the backend pod hangs / crashes / times out mid-import.
        const detail = pollErr?.response?.data?.detail
          || pollErr?.message
          || 'Sin conexión con el servidor durante la importación';
        const err = new Error(detail);
        err.response = { data: { detail: `Error de conexión al verificar el estado: ${detail}` } };
        throw err;
      }
      if (onProgress) onProgress(status.progress_message || status.status, status.rows || 0);
      if (status.status === 'completed') { uploadsApi._clearPending(); return status.upload; }
      if (status.status === 'failed') {
        uploadsApi._clearPending();
        const detail = status.error || status.progress_message || 'Error desconocido durante la importación';
        const err = new Error(detail);
        err.response = { data: { detail } };
        throw err;
      }
    }
  },
  history: () => api.get('/uploads/history').then((r) => r.data),
  remove: (id) => api.delete(`/uploads/${id}`).then((r) => r.data),
  revalidate: (id) => api.post(`/uploads/${id}/revalidate`).then((r) => r.data),
  /**
   * Pre-upload inspection. Returns { detected_type, date_range, period_aware,
   * row_count, stores_count, ... } without persisting anything.
   * Used by the Upload page to show "Este archivo cubre 1 ene → 31 ene 2026"
   * before the user picks a mode and confirms.
   */
  validate: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/uploads/validate', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
};

export const analyticsApi = {
  overview: (params = {}) => api.get('/analytics/overview', { params }).then((r) => r.data),
  dateRange: () => api.get('/analytics/date-range').then((r) => r.data),
  salespeople: (params = {}) => api.get('/analytics/salespeople', { params }).then((r) => r.data),
  daily: (params = {}) => api.get('/analytics/daily', { params }).then((r) => r.data),
  brands: (params = {}) => api.get('/analytics/brands', { params }).then((r) => r.data),
  topProducts: (limit = 20) => api.get('/analytics/top-products', { params: { limit } }).then((r) => r.data),
  orders: (params = {}) => api.get('/analytics/orders', { params }).then((r) => r.data),
  // Iter 96.5: accept legacy string `store` OR object `{ store, since, until, seller, region, brand }`.
  _commParams: (storeOrParams) => {
    if (typeof storeOrParams === 'string') {
      return storeOrParams && storeOrParams !== 'todas' ? { store: storeOrParams } : {};
    }
    return storeOrParams || {};
  },
  commissionsOverview: (p) => api.get('/analytics/commissions/overview', { params: analyticsApi._commParams(p) }).then((r) => r.data),
  commissionsBySalesperson: (p) => api.get('/analytics/commissions/by-salesperson', { params: analyticsApi._commParams(p) }).then((r) => r.data),
  commissionsByLocation: (p) => api.get('/analytics/commissions/by-location', { params: analyticsApi._commParams(p) }).then((r) => r.data),
  commissionsByDistrict: (p) => api.get('/analytics/commissions/by-district', { params: analyticsApi._commParams(p) }).then((r) => r.data),
};

export const projectionsApi = {
  goal: (monthlyGoal = 2500000) => api.get('/projections/goal', { params: { monthly_goal: monthlyGoal } }).then((r) => r.data),
};

export const detailApi = {
  seller: (name) => api.get('/analytics/seller-detail', { params: { name } }).then((r) => r.data),
  store: (name) => api.get('/analytics/store-detail', { params: { name } }).then((r) => r.data),
};

export const exportsApi = {
  url: (path) => `${API}/${path}`,
  download: async (path, filename) => {
    const token = tokenStore.get();
    const res = await fetch(`${API}/${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },
};

export const reconciliationApi = {
  // Iter 96.5c: accept legacy string `store` OR object params with FiltersBar fields.
  overview: (p) => {
    const params = typeof p === 'string'
      ? (p && p !== 'todas' ? { store: p } : {})
      : (p || {});
    return api.get('/reconciliation/overview', { params }).then((r) => r.data);
  },
};

export const alertsApi = {
  list: () => api.get('/alerts').then((r) => r.data),
  aiSummary: () => api.post('/ai/alert-summary').then((r) => r.data),
};

export const forecastingApi = {
  // Iter 96.5c: 2nd arg can be a string (store) or an object with extra params.
  sales: (daysAhead = 30, p) => {
    const params = { days_ahead: daysAhead };
    if (typeof p === 'string') {
      if (p && p !== 'todas') params.store = p;
    } else if (p && typeof p === 'object') {
      Object.assign(params, p);
    }
    return api.get('/forecasting/sales', { params }).then((r) => r.data);
  },
};

export const comparisonsApi = {
  period: (params) => api.get('/comparisons/period', { params }).then((r) => r.data),
  aiPeriod: (payload) => api.post('/ai/period-comparison', payload).then((r) => r.data),
};

export const usersApi = {
  list: () => api.get('/users').then((r) => r.data),
  create: (payload) => api.post('/users', payload).then((r) => r.data),
  setRole: (payload) => api.patch('/users/role', payload).then((r) => r.data),
  resetPassword: (payload) => api.post('/users/reset-password', payload).then((r) => r.data),
  delete: (email) => api.delete(`/users/${encodeURIComponent(email)}`).then((r) => r.data),
  markWelcomed: () => api.post('/users/me/welcome').then((r) => r.data),
  setInterests: (interests) => api.post('/users/me/interests', { interests }).then((r) => r.data),
};

export const auditApi = {
  list: (params = {}) => api.get('/audit', { params }).then((r) => r.data),
  exportUrl: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return `${API}/audit/export.csv${q ? '?' + q : ''}`;
  },
  delete: (id) => api.delete(`/audit/${encodeURIComponent(id)}`).then((r) => r.data),
  bulkDelete: (payload) => api.post('/audit/bulk-delete', payload).then((r) => r.data),
};

export const manualApi = {
  download: () => api.get('/manual/pdf', { responseType: 'blob', timeout: 60000 }).then((r) => r.data),
};

export const workspaceApi = {
  list: () => api.get('/workspaces').then((r) => r.data),
  setUserStore: (email, store_code) => api.patch('/users/workspace-store', { email, store_code }).then((r) => r.data),
  cloneFromCorporate: (store_code) => api.post('/workspaces/clone', { store_code }).then((r) => r.data),
  wipe: (workspace, confirm) => api.post('/workspaces/wipe', { workspace, confirm }).then((r) => r.data),
};

export const meApi = {
  performance: () => api.get('/me/performance').then((r) => r.data),
  podium: () => api.get('/me/podium').then((r) => r.data),
  scope: () => api.get('/me/scope').then((r) => r.data),
  // Iter 75 — DISTINCT stores derived from TE395 for the current seller.
  // Used by /mi-desempeno to render the "Mis tiendas" chip + drive any
  // store-specific widget.
  allowedStores: () => api.get('/me/allowed-stores').then((r) => r.data),
  // Open TE326 orders attributed to the logged-in seller with a pending
  // balance — sorted desc by balance for the "Clientes a perseguir" widget.
  clientsToChase: (params = {}) => api.get('/me/clients-to-chase', { params }).then((r) => r.data),
};

export const ar317Api = {
  // Iter 96.5c: accept legacy string `store` OR object `{ store, region }`.
  _params: (storeOrParams) => {
    if (typeof storeOrParams === 'string') {
      return storeOrParams && storeOrParams !== 'todas' ? { store: storeOrParams } : {};
    }
    return storeOrParams || {};
  },
  overview: (p) => api.get('/ar317/overview', { params: ar317Api._params(p) }).then((r) => r.data),
  dates: (p) => api.get('/ar317/dates', { params: ar317Api._params(p) }).then((r) => r.data),
  comparison: (p) => api.get('/ar317/comparison', { params: ar317Api._params(p) }).then((r) => r.data),
  byStore: (p) => api.get('/ar317/by-store', { params: ar317Api._params(p) }).then((r) => r.data),
  byPayment: (p) => api.get('/ar317/by-payment', { params: ar317Api._params(p) }).then((r) => r.data),
  byPayClass: (p) => api.get('/ar317/by-pay-class', { params: ar317Api._params(p) }).then((r) => r.data),
  topCustomers: (limit = 15, p) => api.get('/ar317/top-customers', {
    params: { limit, ...ar317Api._params(p) }
  }).then((r) => r.data),
  transactions: (params = {}) => api.get('/ar317/transactions', { params }).then((r) => r.data),
};

export const te326Api = {
  // Iter 96.5: accept legacy string `store` OR object `{ store, since, until, seller, region }`.
  _params: (storeOrParams) => {
    if (typeof storeOrParams === 'string') {
      return storeOrParams && storeOrParams !== 'todas' ? { store: storeOrParams } : {};
    }
    return storeOrParams || {};
  },
  overview: (p) => api.get('/te326/overview', { params: te326Api._params(p) }).then((r) => r.data),
  byStatus: (p) => api.get('/te326/by-status', { params: te326Api._params(p) }).then((r) => r.data),
  bySource: (p) => api.get('/te326/by-source', { params: te326Api._params(p) }).then((r) => r.data),
  byDate: (p) => api.get('/te326/by-date', { params: te326Api._params(p) }).then((r) => r.data),
  byStore: () => api.get('/te326/by-store').then((r) => r.data),
  aging: (p) => api.get('/te326/aging', { params: te326Api._params(p) }).then((r) => r.data),
  transactions: (params = {}) => api.get('/te326/transactions', { params }).then((r) => r.data),
  crossTe301: (p) => api.get('/te326/cross-te301', { params: te326Api._params(p) }).then((r) => r.data),
  calendar: (params = {}) => api.get('/te326/calendar', { params }).then((r) => r.data),
  calendarDay: (params = {}) => api.get('/te326/calendar-day', { params }).then((r) => r.data),
};

export const ic338Api = {
  overview: (storeOrParams) => {
    // Accept legacy string (store) or new object form { store, brand, category }.
    const params = typeof storeOrParams === 'string'
      ? (storeOrParams && storeOrParams !== 'todas' ? { store: storeOrParams } : {})
      : (storeOrParams || {});
    return api.get('/ic338/overview', { params }).then((r) => r.data);
  },
  aging: (params = {}) => api.get('/ic338/aging', { params }).then((r) => r.data),
  byWarehouse: () => api.get('/ic338/by-warehouse').then((r) => r.data),
  byBrand: (params = {}) => api.get('/ic338/by-brand', { params }).then((r) => r.data),
  byCategory: (storeOrParams) => {
    const params = typeof storeOrParams === 'string'
      ? (storeOrParams && storeOrParams !== 'todas' ? { store: storeOrParams } : {})
      : (storeOrParams || {});
    return api.get('/ic338/by-category', { params }).then((r) => r.data);
  },
  deadStock: (params = {}) => api.get('/ic338/dead-stock', { params }).then((r) => r.data),
  crossTe395: () => api.get('/ic338/cross-te395').then((r) => r.data),
  items: (params = {}) => api.get('/ic338/items', { params }).then((r) => r.data),
  // Executive reports
  oldest: (params = {}) => api.get('/ic338/exec/oldest', { params }).then((r) => r.data),
  bestsellers: (params = {}) => api.get('/ic338/exec/bestsellers', { params }).then((r) => r.data),
  byRegion: () => api.get('/ic338/exec/by-region').then((r) => r.data),
  heatmap: () => api.get('/ic338/exec/heatmap-cat-aging').then((r) => r.data),
  outletCandidates: (params = {}) => api.get('/ic338/exec/outlet-candidates', { params }).then((r) => r.data),
  storesSellingOld: () => api.get('/ic338/exec/stores-selling-old').then((r) => r.data),
  storesGaps: () => api.get('/ic338/exec/stores-not-selling-categories').then((r) => r.data),
  aiInsights: () => api.get('/ic338/exec/ai-insights', { timeout: 90000 }).then((r) => r.data),
  pdfUrl: (includeAi = true) => `${API}/ic338/exec/pdf?include_ai=${includeAi ? 'true' : 'false'}`,
  // FASE B2 · Inventario sin ventas configurable
  zeroSales: (days = 90, params = {}) => api.get('/inventory/zero-sales', { params: { days, ...params } }).then((r) => r.data),
};

// FASE B3 · Pagos sin orden + IA Match
export const paymentsApi = {
  // Iter 96.5e: accept legacy string `store` OR object with FiltersBar params.
  orphans: (p) => {
    const params = typeof p === 'string'
      ? (p && p !== 'todas' ? { store: p } : {})
      : (p || {});
    return api.get('/payments/orphans', { params }).then((r) => r.data);
  },
  aiMatch: (items) => api.post('/payments/orphans/ai-match', { items }, { timeout: 90000 }).then((r) => r.data),
};

// Profit / Margin con semáforo
export const profitMarginApi = {
  // Iter 96.5e: extra param object accepts since/until/seller/region/brand from FiltersBar.
  fetch: (scope = 'store', limit = 50, extra = {}) =>
    api.get('/reports/profit-margin', { params: { scope, limit, ...extra } }).then((r) => r.data),
};

// Dashboard Visual · solo gráficas
export const visualDashboardApi = {
  fetch: () => api.get('/dashboard/visual', { timeout: 60000 }).then((r) => r.data),
  score: () => api.get('/dashboard/score').then((r) => r.data),
};

// Gamification · leaderboard mensual
export const gamificationApi = {
  leaderboard: (limit = 20) =>
    api.get('/gamification/leaderboard', { params: { limit }, timeout: 60000 }).then((r) => r.data),
  sellersChart: (limit = 10) =>
    api.get('/gamification/sellers-chart', { params: { limit }, timeout: 60000 }).then((r) => r.data),
};

// Inventario · productos antigüedad ≥N días
export const inventoryAgedApi = {
  fetch: (minDays = 30, limit = 500) =>
    api.get('/inventory/aged', { params: { min_days: minDays, limit } }).then((r) => r.data),
};

export const aiApi = {
  salesTips: (scope = 'overall') => api.post('/ai/sales-tips', { scope }).then((r) => r.data),
  sellerTip: (payload) => api.post('/ai/seller-tip', payload).then((r) => r.data),
  chat: (payload) => api.post('/ai/chat', payload).then((r) => r.data),
  chatHistory: () => api.get('/ai/chat/history').then((r) => r.data),
  chatSession: (id) => api.get(`/ai/chat/history/${id}`).then((r) => r.data),
  deleteChat: (id) => api.delete(`/ai/chat/history/${id}`).then((r) => r.data),
  suggestDailyTarget: (store) => api.get('/ai/suggest-daily-target', {
    params: store && store !== 'todas' ? { store } : {},
  }).then((r) => r.data),
  // FASE 3: TTS, motivational quotes, personal coach
  tts: (text, voice = 'nova') => api.post('/ai/tts', { text, voice }, { responseType: 'blob', timeout: 60000 }).then((r) => r.data),
  motivationalQuotes: (refresh = false) => api.get('/ai/motivational-quotes', { params: refresh ? { refresh: true } : {} }).then((r) => r.data),
  personalCoach: (sellerName) => api.get('/ai/personal-coach', {
    params: sellerName ? { seller_name: sellerName } : {},
  }).then((r) => r.data),
};

export const settingsApi = {
  get: () => api.get('/settings').then((r) => r.data),
  update: (payload) => api.patch('/settings', payload).then((r) => r.data),
};

export const searchApi = {
  global: (q) => api.get('/search', { params: { q } }).then((r) => r.data),
};

export const catalogApi = {
  stores: () => api.get('/catalog/stores').then((r) => r.data),
  sellers: () => api.get('/catalog/sellers').then((r) => r.data),
  createStore: (data) => api.post('/catalog/stores', data).then((r) => r.data),
  updateStore: (code, data) => api.patch(`/catalog/stores/${code}`, data).then((r) => r.data),
  deleteStore: (code) => api.delete(`/catalog/stores/${code}`).then((r) => r.data),
  bulkStoreDistricts: (items) => api.put('/catalog/stores/districts', { items }).then((r) => r.data),
  // Districts (catálogo aparte)
  districts: () => api.get('/catalog/districts').then((r) => r.data),
  importStores: () => api.post('/catalog/stores/import').then((r) => r.data),
  importSellers: () => api.post('/catalog/sellers/import').then((r) => r.data),
  createDistrict: (data) => api.post('/catalog/districts', data).then((r) => r.data),
  importDistricts: () => api.post('/catalog/districts/import').then((r) => r.data),
  importAll: () => api.post('/catalog/import-all').then((r) => r.data),
  updateDistrict: (code, data) => api.patch(`/catalog/districts/${code}`, data).then((r) => r.data),
  deleteDistrict: (code) => api.delete(`/catalog/districts/${code}`).then((r) => r.data),
  createSeller: (data) => api.post('/catalog/sellers', data).then((r) => r.data),
  updateSeller: (id, data) => api.patch(`/catalog/sellers/${id}`, data).then((r) => r.data),
  deleteSeller: (id) => api.delete(`/catalog/sellers/${id}`).then((r) => r.data),
  // Franchises (iter 46)
  franchises: () => api.get('/catalog/franchises').then((r) => r.data),
  createFranchise: (data) => api.post('/catalog/franchises', data).then((r) => r.data),
  updateFranchise: (code, data) => api.patch(`/catalog/franchises/${code}`, data).then((r) => r.data),
  deleteFranchise: (code) => api.delete(`/catalog/franchises/${code}`).then((r) => r.data),
  seedFranchises: () => api.post('/catalog/franchises/seed-from-stores').then((r) => r.data),
};

export const franchisesApi = {
  overview: () => api.get('/franchises/overview').then((r) => r.data),
};

// Admin: manage tokenized public share-links to the executive dashboard.
export const shareLinksApi = {
  list: () => api.get('/admin/share-links').then((r) => r.data),
  create: ({ label, expires_in_days, role, scope_value, scope_values, filters, visible_slides, pin, kind, editable }) =>
    api.post('/admin/share-links', {
      label, expires_in_days, role, scope_value, scope_values, filters, visible_slides, pin, kind, editable,
    }).then((r) => r.data),
  revoke: (token) => api.delete(`/admin/share-links/${token}`).then((r) => r.data),
  hardDelete: (token) => api.delete(`/admin/share-links/${token}/hard`).then((r) => r.data),
  purge: (scope = 'revoked') => api.post(`/admin/share-links/purge?scope=${scope}`).then((r) => r.data),
  scopeOptions: () => api.get('/admin/share-links/scope-options').then((r) => r.data),
  updatePin: (token, pin) =>
    api.patch(`/admin/share-links/${token}/pin`, { pin: pin || null }).then((r) => r.data),
};

export const publicChallengesApi = {
  list: (token) =>
    publicAxios.get(`/public/challenges/${token}`).then((r) => r.data),
  create: (token, payload) =>
    publicAxios.post(`/public/challenges/${token}`, payload).then((r) => r.data),
  patch: (token, challengeId, payload) =>
    publicAxios.patch(`/public/challenges/${token}/${challengeId}`, payload).then((r) => r.data),
  remove: (token, challengeId) =>
    publicAxios.delete(`/public/challenges/${token}/${challengeId}`).then((r) => r.data),
  notifyTeams: (token, challengeId) =>
    publicAxios.post(`/public/challenges/${token}/${challengeId}/notify-teams`).then((r) => r.data),
};

// Admin-only data quality reports (iter 50.4)
export const dataQualityApi = {
  summary: () => api.get('/admin/data-quality/summary').then((r) => r.data),
  suspiciousPrices: (params = {}) =>
    api.get('/admin/data-quality/suspicious-prices', { params }).then((r) => r.data),
  stockMismatch: (params = {}) =>
    api.get('/admin/data-quality/stock-mismatch', { params }).then((r) => r.data),
  // iter 53 — approval queue for suggested prices
  approvePriceOverride: (sku, value, reason) =>
    api.post(`/admin/data-quality/price-override/${encodeURIComponent(sku)}`, { value, reason }).then((r) => r.data),
  revertPriceOverride: (sku) =>
    api.delete(`/admin/data-quality/price-override/${encodeURIComponent(sku)}`).then((r) => r.data),
  // iter 53.1 — change history
  priceOverrideLog: (params = {}) =>
    api.get('/admin/data-quality/price-override/log', { params }).then((r) => r.data),
  // iter 65 — sets quality check
  setsQuality: (params = {}) =>
    api.get('/admin/data-quality/sets-quality', { params }).then((r) => r.data),
};

export const auditLogsApi = {
  list: (params = {}) => api.get('/admin/audit-logs', { params }).then((r) => r.data),
  delete: (id) => api.delete(`/admin/audit-logs/${id}`).then((r) => r.data),
  purge: (scope = 'older_than_90d') => api.post(`/admin/audit-logs/purge?scope=${scope}`).then((r) => r.data),
};

export const shareLinkViewsApi = {
  list: (params = {}) => api.get('/admin/share-link-views', { params }).then((r) => r.data),
  purge: (scope = 'older_than_90d') => api.post(`/admin/share-link-views/purge?scope=${scope}`).then((r) => r.data),
};

// Product catalog (SKU → image_url, retail price, stock) — used to enrich
// every UI that lists products with thumbnails and metadata.
export const productsApi = {
  stats: () => api.get('/product-catalog/stats').then((r) => r.data),
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/product-catalog/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  get: (sku) => api.get(`/products/${encodeURIComponent(sku)}`).then((r) => r.data),
  lookup: (skus) => api.post('/products/lookup', skus).then((r) => r.data),
  list: (params = {}) => api.get('/products', { params }).then((r) => r.data),
  deadStock: (params = {}) => api.get('/inventory/dead-stock', { params }).then((r) => r.data),
};

// Challenges · Reto del día (pizza/tacos/spif/%/custom)
export const challengesApi = {
  list: (params = {}) => api.get('/challenges', { params }).then((r) => r.data),
  scopeOptions: () => api.get('/challenges/scope-options').then((r) => r.data),
  create: (payload) => api.post('/challenges', payload).then((r) => r.data),
  update: (id, payload) => api.patch(`/challenges/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/challenges/${id}`).then((r) => r.data),
  progress: (id) => api.get(`/challenges/${id}/progress`).then((r) => r.data),
  notifyTeams: (id) => api.post(`/challenges/${id}/notify-teams`).then((r) => r.data),
};

// Public (no auth) — used by the /d/:token routes.
const publicAxios = axios.create({ baseURL: API });

// Per-tab cache of verified pin-tokens (one key per share token).
// SessionStorage so the PIN gate doesn't survive a tab close but DOES survive
// a refresh inside the same tab.
const pinTokenStore = {
  key: (shareToken) => `canales_pin_token:${shareToken}`,
  get: (shareToken) => {
    try { return sessionStorage.getItem(pinTokenStore.key(shareToken)) || ''; }
    catch { return ''; }
  },
  set: (shareToken, val) => {
    try { sessionStorage.setItem(pinTokenStore.key(shareToken), val || ''); }
    catch { /* ignore */ }
  },
  clear: (shareToken) => {
    try { sessionStorage.removeItem(pinTokenStore.key(shareToken)); }
    catch { /* ignore */ }
  },
};

// Inject X-Share-Pin-Token automatically when calling any
// /public/dashboard/{token}/* OR /public/products/{token}/* endpoint, based
// on the token in the URL.
//
// IMPORTANT: the alternation list below (dashboard|products|challenges) MUST
// stay in sync with backend/share_links.py public endpoints. If you add a new
// /public/<kind>/* endpoint, append it here or the X-Share-Pin-Token header
// will not be attached and PIN-protected links will return 401 pin_required.
publicAxios.interceptors.request.use((config) => {
  try {
    const url = config.url || '';
    const m = url.match(/\/public\/(?:dashboard|products|challenges)\/([^/?]+)/);
    if (m && m[1]) {
      const t = pinTokenStore.get(m[1]);
      if (t) {
        config.headers = config.headers || {};
        config.headers['X-Share-Pin-Token'] = t;
      }
    }
  } catch { /* ignore */ }
  return config;
});

export const publicDashboardApi = {
  meta: (token) => publicAxios.get(`/public/dashboard/${token}/meta`).then((r) => r.data),
  verifyPin: (token, pin) =>
    publicAxios.post(`/public/dashboard/${token}/verify-pin`, { pin }).then((r) => {
      const pt = r.data?.pin_token;
      if (pt) pinTokenStore.set(token, pt);
      return r.data;
    }),
  /**
   * Bypass the PIN gate when the caller has an admin JWT in sessionStorage
   * AND is either the link's creator or a workspace admin.
   *
   * The admin JWT is read via the AUTHENTICATED `api` axios instance (which
   * already injects the Authorization header), so this MUST be called via
   * the regular `api` client, not `publicAxios`.
   */
  adminBypass: (token) =>
    api.post(`/public/dashboard/${token}/admin-bypass`).then((r) => {
      const pt = r.data?.pin_token;
      if (pt) pinTokenStore.set(token, pt);
      return r.data;
    }),
  clearPinToken: (token) => pinTokenStore.clear(token),
  getPinToken: (token) => pinTokenStore.get(token),
  dashboard: (token) => publicAxios.get(`/public/dashboard/${token}`).then((r) => r.data),
  seller: (token, sellerId) =>
    publicAxios.get(`/public/dashboard/${token}/sellers/${encodeURIComponent(sellerId)}`).then((r) => r.data),
  store: (token, storeName) =>
    publicAxios.get(`/public/dashboard/${token}/stores/${encodeURIComponent(storeName)}`).then((r) => r.data),
  products: (token, skus) =>
    publicAxios.post(`/public/dashboard/${token}/products`, skus).then((r) => r.data),
  chat: (token, { message, session_id, history }) =>
    publicAxios.post(`/public/dashboard/${token}/chat`, { message, session_id, history }).then((r) => r.data),
  // Iter 96.8b — Goals vs Actual slide (public dashboard read-only)
  goalsVsActual: (token, params = {}) =>
    publicAxios.get(`/public/dashboard/${token}/goals-vs-actual`, { params }).then((r) => r.data),
  // Iter 96.7: Reporte ejecutivo profundo. POST starts a background job
  // (returns immediately with job_id); we poll the status endpoint until
  // status == 'done' or 'error'. Bypasses the 60s preview ingress timeout.
  deepReport: (token) =>
    publicAxios.post(`/public/dashboard/${token}/deep-report`).then((r) => r.data),
  deepReportStatus: (token, jobId) =>
    publicAxios.get(`/public/dashboard/${token}/deep-report-status/${jobId}`, {
      // Iter 96.7 r3: short timeout — if the backend is briefly busy we just
      // retry, instead of letting the ingress 502 us at 30-60s.
      timeout: 10_000,
    }).then((r) => r.data),
};

// Public Top 10 rankings board (no auth) — used by /p/top10/:token route.
// Same X-Share-Pin-Token interceptor as publicDashboardApi (publicAxios).
export const publicTop10Api = {
  info: (token) => publicAxios.get(`/public/top10/${token}/info`).then((r) => r.data),
  data: (token, params = {}) =>
    publicAxios.get(`/public/top10/${token}/data`, { params }).then((r) => r.data),
};

// Public product catalog (no auth) — used by /p/productos/:token route.
// Same X-Share-Pin-Token interceptor as publicDashboardApi (publicAxios).
export const publicProductsApi = {
  catalog: (token, params = {}) =>
    publicAxios.get(`/public/products/${token}`, { params }).then((r) => r.data),
  request: (token, payload) =>
    publicAxios.post(`/public/products/${token}/request`, payload).then((r) => r.data),
  detail: (token, sku) =>
    publicAxios.get(`/public/products/${token}/detail/${encodeURIComponent(sku)}`).then((r) => r.data),
  listCollections: (token, params = {}) =>
    publicAxios.get(`/public/products/${token}/collections`, { params }).then((r) => r.data),
  getCollection: (token, slug, params = {}) =>
    publicAxios.get(`/public/products/${token}/collections/${encodeURIComponent(slug)}`, { params }).then((r) => r.data),
};

// Lead Tracking · admin/manager view of product requests.
export const leadsApi = {
  list: (params = {}) =>
    api.get('/leads', { params }).then((r) => r.data),
  stats: (days = 30) =>
    api.get('/leads/stats', { params: { days } }).then((r) => r.data),
  detail: (leadId) =>
    api.get(`/leads/${encodeURIComponent(leadId)}`).then((r) => r.data),
  updateStatus: (leadId, status, note) =>
    api.patch(`/leads/${encodeURIComponent(leadId)}/status`, { status, note }).then((r) => r.data),
  appendNote: (leadId, note) =>
    api.patch(`/leads/${encodeURIComponent(leadId)}/note`, { note }).then((r) => r.data),
};

// Web Catalog · "Concentrado de web" + Sets de Mercancía.
export const webCatalogApi = {
  status: () => api.get('/web-catalog/status').then((r) => r.data),
  /**
   * Iter 80 — Upload returns IMMEDIATELY with a job_id; the actual import
   * runs in the background. The caller should then poll `importStatus(job_id)`
   * every ~2s until status is "completed" or "failed".
   *
   * @param file CSV File
   * @param onProgress called with upload byte progress (0..100)
   */
  upload: (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/web-catalog/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    }).then((r) => r.data);
  },
  importStatus: (jobId) =>
    api.get(`/web-catalog/import-status/${jobId}`).then((r) => r.data),
  searchProducts: (q, vendor, limit = 20) =>
    api.get('/web-catalog/products', { params: { q, vendor, limit } }).then((r) => r.data),
  productDetail: (handle) =>
    api.get(`/web-catalog/products/${encodeURIComponent(handle)}`).then((r) => r.data),
  // Iter 83 — Storefront-style collections (matches canalesfurniture.com)
  listCollections: (params = {}) =>
    api.get('/web-catalog/collections', { params }).then((r) => r.data),
  getCollection: (slug, params = {}) =>
    api.get(`/web-catalog/collections/${encodeURIComponent(slug)}`, { params }).then((r) => r.data),
  // Iter 93 — Tags audit for admins
  tagsAudit: () =>
    api.get('/web-catalog/tags-audit').then((r) => r.data),
  listSets: (params = {}) =>
    api.get('/web-catalog/sets', { params }).then((r) => r.data),
  globalStats: (params = {}) =>
    api.get('/web-catalog/sets/stats', { params }).then((r) => r.data),
  generateScene: (setId, memberHandles = null) => {
    // memberHandles: optional array — when provided, the scene is generated
    // using ONLY those member handles (so deselecting a piece can re-render
    // the scene without it).
    const body = memberHandles ? { member_handles: memberHandles } : null;
    return api.post(`/web-catalog/sets/${encodeURIComponent(setId)}/generate-scene`, body, {
      timeout: 120000,
    }).then((r) => r.data);
  },
  uploadScene: (setId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/web-catalog/sets/${encodeURIComponent(setId)}/upload-scene`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000,
    }).then((r) => r.data);
  },
  deleteScene: (setId) =>
    api.delete(`/web-catalog/sets/${encodeURIComponent(setId)}/scene`).then((r) => r.data),
  setsWithoutScene: (limit = 200, allSets = false) =>
    api.get('/web-catalog/sets/without-scene', {
      params: { limit, all_sets: allSets },
    }).then((r) => r.data),
  analytics: (params = {}) =>
    api.get('/web-catalog/sets/analytics', { params }).then((r) => r.data),
  sendAnalyticsToTeams: (days = 7) =>
    api.post('/web-catalog/sets/analytics/send-to-teams', null, { params: { days } }).then((r) => r.data),
  getSet: (setId) =>
    api.get(`/web-catalog/sets/${encodeURIComponent(setId)}`).then((r) => r.data),
  createSet: (payload) =>
    api.post('/web-catalog/sets', payload).then((r) => r.data),
  updateSet: (setId, payload) =>
    api.patch(`/web-catalog/sets/${encodeURIComponent(setId)}`, payload).then((r) => r.data),
  deleteSet: (setId) =>
    api.delete(`/web-catalog/sets/${encodeURIComponent(setId)}`).then((r) => r.data),
  addMember: (setId, handle) =>
    api.post(`/web-catalog/sets/${encodeURIComponent(setId)}/members`, { handle }).then((r) => r.data),
  removeMember: (setId, handle) =>
    api.delete(`/web-catalog/sets/${encodeURIComponent(setId)}/members/${encodeURIComponent(handle)}`).then((r) => r.data),
};
